import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { writeLastSiteId } from '@utils/lastSite';
import type { CameraGridSize } from '@interfaces/ICameras';
import { MOCK_CAMERAS, MOCK_CAMERA_EVENTS } from '@src/mocks/cameras';
import { AlertIcon } from '@utils/icons';
import { CameraList } from './CameraList/CameraList';
import { CameraFeed } from './CameraFeed/CameraFeed';
import { EventsPanel } from './EventsPanel/EventsPanel';
import { CameraModal } from './CameraModal/CameraModal';
import { floorRank } from './utils/floorRank';
import { PageActions } from '@components/ui/PageFrame/PageFrame';
import styles from './CamerasPage.module.css';

export function CamerasPage() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();

  const [search, setSearch] = useState('');
  const [floorFilter, setFloorFilter] = useState<string | null>(null);
  const [gridSize, setGridSize] = useState<CameraGridSize>(3);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [eventsOpen, setEventsOpen] = useState(true);

  useEffect(() => {
    if (siteId) writeLastSiteId(siteId);
  }, [siteId]);

  // Frontend-only mock — swap these two for a real loader when a camera
  // adapter exists (see src/mocks/cameras.ts).
  const cameras = MOCK_CAMERAS;
  const events = MOCK_CAMERA_EVENTS;

  // Summary counts for the header and filters, derived from the camera list.
  const counts = useMemo(() => ({
    total: cameras.length,
    live: cameras.filter((c) => c.status === 'live').length,
    alerts: cameras.filter((c) => c.status === 'alert').length,
  }), [cameras]);

  // Floor chips, derived from the camera data and ordered by floor level.
  const floorOptions = useMemo(() => {
    const byFloor = new Map<string, number>();
    for (const c of cameras) byFloor.set(c.floor, c.level);
    return Array.from(byFloor.keys()).sort(
      (a, b) => floorRank(byFloor.get(a)!) - floorRank(byFloor.get(b)!),
    );
  }, [cameras]);

  // Apply search + floor filters to the camera list, then sort by floor.
  const visibleCameras = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = cameras.filter((c) => {
      if (floorFilter !== null && c.floor !== floorFilter) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q) ||
        c.zone.toLowerCase().includes(q) ||
        c.floor.toLowerCase().includes(q)
      );
    });

    return [...filtered].sort((a, b) => {
      const byFloor = floorRank(a.level) - floorRank(b.level);
      return byFloor !== 0 ? byFloor : a.id.localeCompare(b.id);
    });
  }, [cameras, search, floorFilter]);

  return (
    <>
      <PageActions>
        <button
          type="button"
          className={styles.eventsToggle}
          data-active={eventsOpen}
          onClick={() => setEventsOpen((open) => !open)}
          aria-pressed={eventsOpen}
        >
          <AlertIcon size={15} />
          {t('cameras.events.title')}
          {counts.alerts > 0 && <span className={styles.eventsCount}>{counts.alerts}</span>}
        </button>
      </PageActions>
      <div className={styles.body} data-events-open={eventsOpen}>
        <CameraList
          cameras={visibleCameras}
          counts={counts}
          search={search}
          onSearchChange={setSearch}
          floorOptions={floorOptions}
          floorFilter={floorFilter}
          onFloorFilterChange={setFloorFilter}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />

        <CameraFeed
          cameras={visibleCameras}
          gridSize={gridSize}
          onGridSizeChange={setGridSize}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onExpand={setExpandedId}
        />

        {eventsOpen && (
          <EventsPanel events={events} onClose={() => setEventsOpen(false)} />
        )}
      </div>

      <CameraModal
        camera={cameras.find((c) => c.id === expandedId) ?? null}
        onClose={() => setExpandedId(null)}
      />
    </>
  );
}
